import { useEffect, useRef, useMemo, useCallback } from 'react';
import { Box } from '@mui/material';
import { EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Placeholder from '@tiptap/extension-placeholder';
import { useSelectionStore } from '../../stores/selection.store';
import { useUpdateParagraph, type ParagraphDto } from '../../api/queries/books';
import {
  useCreateParagraph,
  useDeleteParagraph,
  useSplitParagraph,
  useMergeParagraphs,
} from '../../api/queries/paragraphs';
import { useAutosave } from '../../hooks/useAutosave';
import {
  createEntityMention,
  type EntityLookup,
} from './extensions/entityMention';

interface TipTapEditorProps {
  paragraphs: ParagraphDto[];
  chapterId: string;
  entityLookup?: EntityLookup;
}

type Pending =
  | { kind: 'update'; id: string; contentHtml: string }
  | {
      kind: 'create';
      tempKey: string;
      contentHtml: string;
      sequenceOrder: number;
    }
  | { kind: 'delete'; id: string };

/**
 * TipTap editor that renders a chapter's paragraphs as a single document. Each paragraph
 * carries data-paragraph-id. The editor diffs the rendered DOM against the known
 * paragraph list on every update: unknown p nodes become POST /paragraphs, missing ids
 * become DELETE /paragraphs/{id}, and changed content becomes updates. All changes are
 * funneled through useAutosave for debouncing.
 *
 * Sprint 4 additions:
 * - Entity mention via @tiptap/extension-mention (type @ to search entities)
 * - Placeholder extension for empty paragraphs
 * - Split paragraph: Enter at mid-paragraph calls the split API directly
 * - Merge paragraph: Backspace at start of paragraph merges with previous
 */
export function TipTapEditor({
  paragraphs,
  chapterId,
  entityLookup,
}: TipTapEditorProps) {
  const select = useSelectionStore((s) => s.select);
  const updateParagraph = useUpdateParagraph();
  const createParagraph = useCreateParagraph();
  const deleteParagraph = useDeleteParagraph(chapterId);
  const splitParagraph = useSplitParagraph(chapterId);
  const mergeParagraphs = useMergeParagraphs(chapterId);
  const pending = useRef<Map<string, Pending>>(new Map());
  const knownIds = useRef<Set<string>>(new Set(paragraphs.map((p) => p.id)));
  const byId = useRef<Map<string, ParagraphDto>>(
    new Map(paragraphs.map((p) => [p.id, p])),
  );

  useEffect(() => {
    knownIds.current = new Set(paragraphs.map((p) => p.id));
    byId.current = new Map(paragraphs.map((p) => [p.id, p]));
  }, [paragraphs]);

  const initialHtml = useMemo(
    () =>
      paragraphs
        .map(
          (p) =>
            `<p data-paragraph-id="${p.id}">${stripOuterP(p.contentHtml)}</p>`,
        )
        .join(''),
    [paragraphs],
  );

  const defaultLookup: EntityLookup = useCallback(() => [], []);
  const lookup = entityLookup ?? defaultLookup;

  const entityMention = useMemo(() => createEntityMention(lookup), [lookup]);

  const editor = useEditor(
    {
      extensions: [
        StarterKit.configure({
          strike: {},
          horizontalRule: {},
        }),
        Placeholder.configure({
          placeholder: 'Start writing...',
        }),
        entityMention,
      ],
      content: initialHtml,
      editorProps: {
        handleClick(_view, _pos, event) {
          const target = event.target as HTMLElement;
          const wrapper = target.closest(
            '[data-paragraph-id]',
          ) as HTMLElement | null;
          if (wrapper?.dataset?.paragraphId) {
            select(wrapper.dataset.paragraphId);
          }
          return false;
        },
        handleKeyDown(view, event) {
          // Split paragraph: Enter at mid-paragraph position
          if (event.key === 'Enter' && !event.shiftKey && !event.metaKey) {
            const { state } = view;
            const { $from } = state.selection;
            const paragraphNode = $from.node($from.depth);

            // Only intercept if inside a paragraph with data-paragraph-id
            if (paragraphNode?.type.name === 'paragraph') {
              const dom = view.nodeDOM($from.before($from.depth));
              const wrapper =
                dom instanceof HTMLElement
                  ? (dom.closest('[data-paragraph-id]') as HTMLElement | null)
                  : null;
              const paragraphId = wrapper?.dataset?.paragraphId;

              if (paragraphId && knownIds.current.has(paragraphId)) {
                // Compute HTML fragments from the editor state
                const start = $from.start($from.depth);
                const cursorOffset = $from.pos - start;
                const fullText = paragraphNode.textContent;

                if (cursorOffset > 0 && cursorOffset < fullText.length) {
                  event.preventDefault();
                  const firstText = fullText.slice(0, cursorOffset);
                  const secondText = fullText.slice(cursorOffset);
                  splitParagraph.mutate({
                    id: paragraphId,
                    firstHtml: `<p>${firstText}</p>`,
                    secondHtml: `<p>${secondText}</p>`,
                  });
                  return true;
                }
              }
            }
          }

          // Merge paragraph: Backspace at start of paragraph
          if (event.key === 'Backspace' && !event.shiftKey && !event.metaKey) {
            const { state } = view;
            const { $from } = state.selection;
            const paragraphNode = $from.node($from.depth);

            if (paragraphNode?.type.name === 'paragraph') {
              const start = $from.start($from.depth);
              const cursorOffset = $from.pos - start;

              if (cursorOffset === 0) {
                const dom = view.nodeDOM($from.before($from.depth));
                const wrapper =
                  dom instanceof HTMLElement
                    ? (dom.closest('[data-paragraph-id]') as HTMLElement | null)
                    : null;
                const paragraphId = wrapper?.dataset?.paragraphId;

                if (paragraphId && knownIds.current.has(paragraphId)) {
                  // Find the previous paragraph
                  const paragraphList = Array.from(knownIds.current);
                  const sortedParagraphs = paragraphs
                    .filter((p) => paragraphList.includes(p.id))
                    .sort((a, b) => a.sequenceOrder - b.sequenceOrder);
                  const idx = sortedParagraphs.findIndex(
                    (p) => p.id === paragraphId,
                  );

                  if (idx > 0) {
                    const prevParagraph = sortedParagraphs[idx - 1]!;
                    event.preventDefault();
                    mergeParagraphs.mutate({
                      sourceId: paragraphId,
                      targetId: prevParagraph.id,
                    });
                    return true;
                  }
                }
              }
            }
          }

          return false;
        },
      },
      onUpdate({ editor }) {
        const html = editor.getHTML();
        const doc = new DOMParser().parseFromString(
          `<div>${html}</div>`,
          'text/html',
        );
        const nodes = Array.from(doc.querySelectorAll('p')) as HTMLElement[];
        const seenIds = new Set<string>();
        let order = 0;
        for (const node of nodes) {
          const id = node.dataset.paragraphId;
          const content = node.outerHTML;
          if (id && knownIds.current.has(id)) {
            seenIds.add(id);
            const original = byId.current.get(id);
            if (original && original.contentHtml !== content) {
              pending.current.set(id, {
                kind: 'update',
                id,
                contentHtml: content,
              });
            }
          } else {
            const tempKey =
              node.dataset.tempParagraphKey ??
              (node.dataset.tempParagraphKey = `t-${crypto.randomUUID()}`);
            pending.current.set(`create:${tempKey}`, {
              kind: 'create',
              tempKey,
              contentHtml: content.replace(
                /\sdata-temp-paragraph-key="[^"]+"/g,
                '',
              ),
              sequenceOrder: order,
            });
          }
          order++;
        }

        for (const id of knownIds.current) {
          if (!seenIds.has(id) && !pending.current.has(`delete:${id}`)) {
            pending.current.set(`delete:${id}`, { kind: 'delete', id });
          }
        }
        markDirty();
      },
    },
    [initialHtml],
  );

  useEffect(() => {
    return () => {
      editor?.destroy();
    };
  }, [editor]);

  const { markDirty } = useAutosave(async () => {
    if (pending.current.size === 0) return;
    const entries = Array.from(pending.current.entries());
    pending.current.clear();
    for (const [, op] of entries) {
      try {
        if (op.kind === 'update') {
          await updateParagraph.mutateAsync({
            id: op.id,
            contentHtml: op.contentHtml,
          });
        } else if (op.kind === 'create') {
          const created = await createParagraph.mutateAsync({
            chapterId,
            contentHtml: op.contentHtml,
            sequenceOrder: op.sequenceOrder,
          });
          const node = editor?.view.dom.querySelector(
            `[data-temp-paragraph-key="${op.tempKey}"]`,
          );
          if (node instanceof HTMLElement) {
            node.removeAttribute('data-temp-paragraph-key');
            node.setAttribute('data-paragraph-id', created.id);
            knownIds.current.add(created.id);
          }
        } else if (op.kind === 'delete') {
          await deleteParagraph.mutateAsync(op.id);
          knownIds.current.delete(op.id);
        }
      } catch (error) {
        console.warn('autosave op failed', op, error);
        pending.current.set(
          op.kind === 'create' ? `create:${op.tempKey}` : `${op.kind}:${op.id}`,
          op,
        );
      }
    }
  });

  return (
    <Box
      sx={{
        '& .tiptap-editor .entity-mention': {
          bgcolor: 'rgba(25, 118, 210, 0.12)',
          borderRadius: '4px',
          px: 0.5,
          fontWeight: 500,
          color: 'primary.main',
        },
        '& .tiptap-editor p.is-editor-empty:first-child::before': {
          content: 'attr(data-placeholder)',
          color: 'text.disabled',
          fontStyle: 'italic',
          pointerEvents: 'none',
          float: 'left',
          height: 0,
        },
        '& hr': {
          border: 'none',
          borderTop: '1px dashed',
          borderColor: 'divider',
          my: 3,
          position: 'relative',
          '&::after': {
            content: '"\u00A7"',
            position: 'absolute',
            top: '-10px',
            left: '50%',
            transform: 'translateX(-50%)',
            bgcolor: 'background.paper',
            px: 1,
            color: 'text.disabled',
            fontSize: 14,
          },
        },
      }}
    >
      <EditorContent editor={editor} className="tiptap-editor prose" />
    </Box>
  );
}

function stripOuterP(html: string): string {
  if (!html) return '';
  const match = html.match(/^<p[^>]*>([\s\S]*)<\/p>$/i);
  return match ? match[1]! : html;
}
